import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jsonOrThrow } from '../apiJson';
import { formatCount } from '../categoryLabels';
import { categoryBrowsePath } from '../categoryPaths';
import { trackPreLanderCtaClick, trackSelectCategory } from '../ga';
import { LogoMark } from './LogoMark';
import { PageHead } from './PageHead';
import './PreLanderPage.css';

const PRE_LANDER_HOOK = 'Psst… before you close';
const PRE_LANDER_BADGE = 'New in Malta';

const PRE_LANDER_VARIANTS = [
  {
    id: 'variant1',
    headline: 'What\u2019s cheaper in Malta right now?',
    subhead: 'Search products across local shops in one place.',
    supportLine: (countLabel) => `${countLabel} products. One quick search.`,
    cta: 'Find deals in Malta',
  },
  {
    id: 'variant2',
    headline: 'Find Malta\u2019s best prices before you open another tab.',
    subhead: 'Compare products from local stores in seconds.',
    supportLine: (countLabel) => `Search ${countLabel} products across Malta.`,
    cta: 'Compare prices in Malta now',
  },
  {
    id: 'variant3',
    headline: 'What if the lowest price in Malta is one search away?',
    subhead:
      'Browse local shops, compare products, and spot better deals fast.',
    supportLine: (countLabel) => `${countLabel} products from Maltese stores.`,
    cta: 'Start searching',
  },
  {
    id: 'variant4',
    headline: 'What\u2019s cheaper in Malta right now?',
    subhead:
      'Compare local shops in one place and spot better prices fast.',
    supportLine: (countLabel) => `${countLabel} products, one simple search.`,
    cta: 'See what\u2019s cheaper',
  },
  {
    id: 'variant5',
    headline: 'How much are you overpaying in Malta?',
    subhead: 'Check products across Maltese shops before you buy.',
    supportLine: () => 'The fastest way to compare local prices.',
    cta: 'Compare prices',
  },
  {
    id: 'variant6',
    headline: 'Which shop in Malta has the better deal?',
    subhead:
      'Search local stores and find the cheapest option in seconds.',
    supportLine: (countLabel) => `${countLabel} products available to compare.`,
    cta: 'Find the best deal',
  },
];

const PRE_LANDER_BABY_CATEGORY_SLUG = 'baby-kids-toys';
const PRE_LANDER_PRICE_COLLECTION_SLUGS = [
  'items-below-5',
  'items-between-5-and-10',
];

function pickRandomCopyVariant() {
  const index = Math.floor(Math.random() * PRE_LANDER_VARIANTS.length);
  return PRE_LANDER_VARIANTS[index];
}

function productCountLabel(productCount) {
  return typeof productCount === 'number' && Number.isFinite(productCount)
    ? formatCount(productCount)
    : '\u2026';
}

function buildCategoryChips(categories) {
  const bySlug = new Map(categories.map((category) => [category.slug, category]));
  const regular = categories.filter(
    (category) =>
      category.priceCollection !== true
      && category.slug !== PRE_LANDER_BABY_CATEGORY_SLUG,
  );
  const babyCategory = bySlug.get(PRE_LANDER_BABY_CATEGORY_SLUG);
  const priceCollections = PRE_LANDER_PRICE_COLLECTION_SLUGS.map(
    (slug) => bySlug.get(slug),
  ).filter(Boolean);
  return [
    ...regular,
    ...(babyCategory ? [babyCategory] : []),
    ...priceCollections,
  ];
}

function categoryChipDisplayName(category) {
  const name = (category.name ?? category.slug ?? '').trim();
  if (category.priceCollection === true) {
    return name;
  }
  const firstWord = name.split(/\s+/)[0] ?? name;
  return firstWord.replace(/[,;&]+$/u, '');
}

function categoryChipCount(category) {
  const count = category.count ?? category.productCount;
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
}

function categoryChipAriaLabel(name, count) {
  const formatted = count == null ? '' : formatCount(count);
  if (!formatted) {
    return name;
  }
  return `${name}, ${formatted} products`;
}

export function PreLanderPage() {
  const navigate = useNavigate();
  const [copyVariant] = useState(pickRandomCopyVariant);
  const [productCount, setProductCount] = useState(null);
  const [categoryChips, setCategoryChips] = useState([]);
  const countLabel = productCountLabel(productCount);

  useEffect(() => {
    let cancelled = false;

    fetch('/api/stats.php')
      .then((res) => jsonOrThrow(res))
      .then((data) => {
        if (cancelled) {
          return;
        }
        const products = Number(data?.products);
        if (Number.isFinite(products)) {
          setProductCount(products);
        }
      })
      .catch(() => {
        /* keep fallback support line label */
      });

    fetch('/api/landing-categories.php')
      .then((res) => jsonOrThrow(res))
      .then((data) => {
        if (cancelled) {
          return;
        }
        const categories = Array.isArray(data?.categories) ? data.categories : [];
        setCategoryChips(buildCategoryChips(categories));
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryChips([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function goToHomepage() {
    trackPreLanderCtaClick(copyVariant.id);
    navigate('/');
  }

  return (
    <>
      <PageHead
        title={`${copyVariant.headline} | Shops.mt`}
        description={copyVariant.subhead}
        robots="noindex, nofollow"
      />
      <p className="pre-lander__hook">{PRE_LANDER_HOOK}</p>
      <span className="pre-lander__badge">{PRE_LANDER_BADGE}</span>
      <main className="pre-lander">
        <div className="pre-lander__stack">
          <div className="pre-lander__logo">
            <LogoMark />
          </div>

          <div className="pre-lander__pitch">
            <h1 className="pre-lander__headline">
              <span className="pre-lander__headline-primary">
                {copyVariant.headline}
              </span>
              <span className="pre-lander__headline-secondary">
                {copyVariant.subhead}
              </span>
            </h1>

            <button
              type="button"
              className="pre-lander__cta"
              onClick={goToHomepage}
            >
              {copyVariant.cta}
            </button>
          </div>

          <div className="pre-lander__below-cta">
            <p className="pre-lander__lede">
              {copyVariant.supportLine(countLabel)}
            </p>
            <div className="pre-lander__cta-rail pre-lander__cta-rail--after" aria-hidden="true" />
          </div>
        </div>
      </main>

      {categoryChips.length > 0 && (
        <div className="pre-lander__chips-bar">
          <div className="pre-lander__chips" aria-label="Popular categories">
            {categoryChips.map((category) => {
              const slug = category.slug ?? '';
              const name = category.name ?? slug;
              const label = categoryChipDisplayName(category);
              const count = categoryChipCount(category);
              const isPriceCollection = category.priceCollection === true;
              if (!slug) {
                return null;
              }
              return (
                <Link
                  key={slug}
                  to={categoryBrowsePath(slug)}
                  className={
                    isPriceCollection
                      ? 'pre-lander__chip pre-lander__chip--price'
                      : 'pre-lander__chip'
                  }
                  aria-label={categoryChipAriaLabel(name, count)}
                  onClick={() => trackSelectCategory(slug, 'pre_lander')}
                >
                  <span className="pre-lander__chip-label">{label}</span>
                  {count != null && (
                    <span className="pre-lander__chip-count" aria-hidden="true">
                      {formatCount(count)}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
